<template>
  <div>
    <v-row align="end" class="mb-4">
      <v-col cols="auto" class="d-flex align-center">
        <shift-picker v-model="selectedShifts" multiple />
      </v-col>
    </v-row>
    <v-row justify="center" class="py-15" no-gutters v-if="loading">
      <v-col cols="auto">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
      </v-col>
    </v-row>
    <template v-else>
      <v-data-table
        :headers="headers"
        :items="customSearch(filteredMeals, headers, search)"
        hide-default-footer
        disable-pagination
        class="striped-table ordera-box-shadow"
      >
        <template v-slot:item.num="{ item, index }">
          {{ index + 1 }}
        </template>
        <template v-slot:body.append>
          <tr class="font-weight-bold">
            <td></td>
            <td>{{ $t("total") }}</td>
            <td v-if="selectedShifts.includes(1)">{{ totals.shift1 }}</td>
            <td v-if="selectedShifts.includes(2)">{{ totals.shift2 }}</td>
            <td v-if="selectedShifts.includes(3)">{{ totals.shift3 }}</td>
            <td v-if="selectedShifts.includes(4)">{{ totals.shift4 }}</td>
            <td>{{ totals.total }}</td>
          </tr>
        </template>
      </v-data-table>
    </template>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import hasPrinting from "@/mixins/hasPrinting";
import hasExcel from "@/mixins/hasExcel";

export default {
  name: "CompanyReportTabMeals",
  mixins: [hasPrinting, hasExcel, hasReportPrinting],
  data() {
    return {
      report: null,
      selectedShifts: [1, 2, 3, 4], // Po default sve četiri smene
      loading: false,
    };
  },
  created() {
    this.fetchMeals();
  },
  methods: {
    fetchMeals() {
      this.loading = true;
      reportService
        .getMealsForRestaurant(this.restaurantId, {
          company: this.selectedCompanies,
          date: this.dates.from,
          date2: this.dates.to,
        })
        .then((response) => {
          this.report = response.data;
        })
        .catch((error) => {
          console.error("Error fetching meals:", error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    createTotalsRow() {
      const totalsRow = {
        num: "",
        name: this.$t("total"),
        total: this.totals.total,
        shift1: {
          quantity: 0,
        },
        shift2: {
          quantity: 0,
        },
        shift3: {
          quantity: 0,
        },
        shift4: {
          quantity: 0,
        },
      };

      // Dodaj kolone za smene samo ako su selektovane
      if (this.selectedShifts.includes(1)) {
        totalsRow.shift1.quantity = this.totals.shift1;
      }
      if (this.selectedShifts.includes(2)) {
        totalsRow.shift2.quantity = this.totals.shift2;
      }
      if (this.selectedShifts.includes(3)) {
        totalsRow.shift3.quantity = this.totals.shift3;
      }
      if (this.selectedShifts.includes(4)) {
        totalsRow.shift4.quantity = this.totals.shift4;
      }
      return totalsRow;
    },
  },
  mounted() {
    this.fetchMeals();
  },
  computed: {
    search() {
      return this.$store.state.report.filters.search;
    },
    meals() {
      if (!this.report) return [];
      const processedMeals = {};
      for (const companyName in this.report) {
        const shifts = this.report[companyName];
        for (const shiftName in shifts) {
          const mealsInShift = shifts[shiftName];
          const shiftKey = `shift${parseInt(shiftName, 10)}`;
          for (const mealName in mealsInShift) {
            const mealData = mealsInShift[mealName];
            if (!processedMeals[mealName]) {
              processedMeals[mealName] = {
                id: mealData.id,
                name: this.toLatin(mealName),
                shift1: { quantity: 0, quantity_payed: 0 },
                shift2: { quantity: 0, quantity_payed: 0 },
                shift3: { quantity: 0, quantity_payed: 0 },
                shift4: { quantity: 0, quantity_payed: 0 },
                total: 0,
                total_payed: 0,
              };
            }
            if (!processedMeals[mealName][shiftKey]) {
              processedMeals[mealName][shiftKey] = {
                quantity: 0,
                quantity_payed: 0,
              };
            }
            processedMeals[mealName][shiftKey].quantity += mealData.quantity;
            processedMeals[mealName][shiftKey].quantity_payed += mealData.quantity_payed;
            processedMeals[mealName].total += mealData.quantity;
            processedMeals[mealName].total_payed += mealData.quantity_payed;
          }
        }
      }
      return Object.values(processedMeals);
    },
    filteredMeals() {
      if (!this.selectedShifts || this.selectedShifts.length === 0) {
        return this.meals;
      }

      return this.meals.map((meal) => {
        const filteredMeal = { ...meal, total: 0 };

        // Reset shift values based on selected shifts
        filteredMeal.shift1 = this.selectedShifts.includes(1)
          ? meal.shift1
          : { quantity: 0, quantity_payed: 0 };
        filteredMeal.shift2 = this.selectedShifts.includes(2)
          ? meal.shift2
          : { quantity: 0, quantity_payed: 0 };
        filteredMeal.shift3 = this.selectedShifts.includes(3)
          ? meal.shift3
          : { quantity: 0, quantity_payed: 0 };
        filteredMeal.shift4 = this.selectedShifts.includes(4)
          ? meal.shift4
          : { quantity: 0, quantity_payed: 0 };

        // Recalculate total
        filteredMeal.total =
          filteredMeal.shift1.quantity +
          filteredMeal.shift2.quantity +
          filteredMeal.shift3.quantity +
          filteredMeal.shift4.quantity;

        return filteredMeal;
      });
    },
    totals() {
      const totals = {
        shift1: 0,
        shift2: 0,
        shift3: 0,
        shift4: 0,
        total: 0,
      };
      this.filteredMeals.forEach((meal) => {
        if (meal.shift1) {
          totals.shift1 += meal.shift1.quantity;
        }
        if (meal.shift2) {
          totals.shift2 += meal.shift2.quantity;
        }
        if (meal.shift3) {
          totals.shift3 += meal.shift3.quantity;
        }
        if (meal.shift4) {
          totals.shift4 += meal.shift4.quantity;
        }
        totals.total += meal.total;
      });
      return totals;
    },
    selectedCompanies() {
      return this.$store.state.report.selectedFoodCompanies
        .map((company) => company.id)
        .join(",");
    },
    dates() {
      return this.$store.state.report.filters.dates;
    },
    restaurantId() {
      return this.$store.state.company?.restaurant_id || this.$store.state.company;
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num", print: false },
        { text: this.$t("meals"), value: "name" },
      ];

      const shiftHeaders = [];

      // Dodaj header kolone samo za selektovane smene
      if (this.selectedShifts.includes(1)) {
        shiftHeaders.push({ text: this.$t("shift_one"), value: "shift1.quantity" });
      }
      if (this.selectedShifts.includes(2)) {
        shiftHeaders.push({ text: this.$t("shift_two"), value: "shift2.quantity" });
      }
      if (this.selectedShifts.includes(3)) {
        shiftHeaders.push({ text: this.$t("shift_three"), value: "shift3.quantity" });
      }
      if (this.selectedShifts.includes(4)) {
        shiftHeaders.push({ text: this.$t("shift_four"), value: "shift4.quantity" });
      }

      const totalHeader = { text: this.$t("total"), value: "total" };

      return [...baseHeaders, ...shiftHeaders, totalHeader];
    },
  },
  watch: {
    "dates.from"(newVal, oldVal) {
      this.fetchMeals();
    },
    selectedCompanies(newVal, oldVal) {
      this.fetchMeals();
    },
    isPrinting(newVal, oldVal) {
      if (newVal) {
        const itemsWithTotals = [
          ...this.customSearch(this.filteredMeals, this.headers, this.search),
          this.createTotalsRow(),
        ];
        this.printDocument({
          tables: [
            {
              headers: this.headers.filter((h) => h.print !== false),
              items: itemsWithTotals,
              showCaption: true,
            },
          ],
          title: [this.$t("report"), this.$t("meals")].join(": "),
          subtitle: this.subtitle,
          hideDatetime: true,
        });
      }
    },
    isExcelDownloading() {
      if (this.isExcelDownloading) {
        const itemsForExcel = [
          ...this.customSearch(this.filteredMeals, this.headers, this.search),
          this.createTotalsRow(),
        ];
        this.downloadExcel({
          fileName: [this.$t("report"), this.$t("meals")].join(" ") + ".xlsx",
          sheets: [
            {
              name: [this.$t("report"), this.$t("meals")].join(" "),
              headers: this.headers.filter((h) => h.print !== false),
              items: itemsForExcel,
              title: [this.$t("report"), this.$t("meals")].join(" "),
              subtitle: this.subtitle,
            },
          ],
        });
      }
    },
  },
};
</script>
